import { useState, useEffect } from 'react';
import { useAuth } from '../../../AuthContext';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Refresh, GetApp, ArrowBack, CompareArrows } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { exportStyledExcel } from '../../../utils/excelExport';
import { formatDate as fmtDate } from '../../../utils/dateUtils';

const fmt     = (n) => `₹${(parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtQty  = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const StockTransferReport = () => {
  useSessionValidation();
  const { companyId, branchId, token, companyName, username } = useAuth();
  const history = useHistory();

  const fmt2Date = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const today = new Date();
  const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [toDate, setToDate]     = useState(fmt2Date(today));
  const [data, setData]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [fetched, setFetched]   = useState(false);

  const fetchReport = async () => {
    if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
    setLoading(true);
    try {
      const res = await fetchData('ReportApi', `/StockTransfer/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
      const rows = res.response_data || res || [];
      // Group by SortId — header row has TransferNumber set, items have Id=0
      const grouped = {};
      const order = []; // preserve order
      (Array.isArray(rows) ? rows : []).forEach(row => {
        const key = row.SortId;
        if (!grouped[key]) {
          grouped[key] = {
            id: key,
            transferNumber: null,
            date: null,
            fromBranch: null,
            toBranch: null,
            status: null,
            items: [],
            totalAmount: 0,
            totalQty: 0,
          };
          order.push(key);
        }
        // Header row: has TransferNumber (non-empty)
        if (row.TransferNumber) {
          grouped[key].transferNumber = row.TransferNumber;
          grouped[key].date           = row.StockTransferDate;
          grouped[key].fromBranch     = row.FromBranch;
          grouped[key].toBranch       = row.ToBranch;
          grouped[key].status         = row.Status;
        } else if (row.ItemName) {
          // Item row
          grouped[key].items.push({
            name:   row.ItemName,
            qty:    parseFloat(row.Quantity) || 0,
            rate:   parseFloat(row.Rate) || 0,
            amount: parseFloat(row.Amount) || 0,
          });
          grouped[key].totalAmount += parseFloat(row.Amount) || 0;
          grouped[key].totalQty   += parseFloat(row.Quantity) || 0;
        }
      });
      const result = order.map(k => grouped[k]);
      setData(result);
      setFetched(true);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch stock transfer report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId && branchId && token && fromDate && toDate) {
      if (new Date(fromDate) > new Date(toDate)) {
        setData([]);
        setFetched(false);
        setError('From Date cannot be greater than To Date');
        return;
      }
      fetchReport();
    }
  }, [companyId, branchId, token, fromDate, toDate]);

  const handleExport = async () => {
    if (!data.length) return;
    const generatedAt = new Date().toLocaleString('en-IN');
    const rows = [];
    data.forEach(t => {
      rows.push([t.transferNumber, fmtDate(t.date), t.fromBranch, t.toBranch, t.status || '-', '', '', '']);
      t.items.forEach(item => {
        rows.push(['', '', '', '', '', item.name, fmtQty(item.qty), fmt(item.amount)]);
      });
      rows.push(['', '', '', '', 'Total', '', fmtQty(t.totalQty), fmt(t.totalAmount)]);
      rows.push([]);
    });
    const headers = ['Transfer No', 'Date', 'From Branch', 'To Branch', 'Status', 'Item', 'Qty', 'Amount'];
    const gQty = data.reduce((s, t) => s + t.totalQty, 0);
    const gAmt = data.reduce((s, t) => s + t.totalAmount, 0);
    const totalsRow = ['', '', '', '', 'Grand Total', '', fmtQty(gQty), fmt(gAmt)];

      await exportStyledExcel({
          reportTitle: 'STOCK TRANSFER REPORT',
          sheetName: 'Stock Transfer Report',
          fileName: `StockTransferReport_${fromDate}_${toDate}.xlsx`,
          companyName,
          username,
          fromDate,
          toDate,
          headers: headers,
          rows,
          totalsRow,
          columnWidths: [16,12,16,16,12,28,10,14],
      });
  };

  const statusColor = (s) => {
    const v = (s || '').toLowerCase();
    if (v === 'accepted') return { bg: '#d1fae5', color: '#059669' };
    if (v === 'pending')  return { bg: '#fef3c7', color: '#d97706' };
    if (v === 'rejected') return { bg: '#fee2e2', color: '#dc2626' };
    return { bg: '#f3f4f6', color: '#6b7280' };
  };

  const PRIMARY = '#4a148c';
  const PRIMARY_MID = '#8e24aa';
  const PRIMARY_LT = '#f3e5f5';
  const th  = { padding: '8px 12px', textAlign: 'left',  fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap', borderBottom: '1px solid rgba(255,255,255,0.2)', background: PRIMARY_MID, color: 'white' };
  const thR = { ...th, textAlign: 'right' };
  const td  = { padding: '8px 12px', textAlign: 'left',  fontSize: 12, whiteSpace: 'nowrap' };
  const tdR = { padding: '8px 12px', textAlign: 'right', fontSize: 12, whiteSpace: 'nowrap' };

  const totalTransfers = data.length;
  const grandTotal = data.reduce((s, t) => s + t.totalAmount, 0);

  return (
    <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
      <LogoLoader open={loading} message="Loading stock transfer report..." />
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>

      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: `linear-gradient(135deg, ${PRIMARY} 0%, #6a1b9a 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
          <Tooltip title="Back" arrow>
            <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={{ border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', height: 29, width: 29, color: '#fff', flexShrink: 0, '&:hover': { background: 'rgba(255,255,255,0.1)' } }}>
              <ArrowBack sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>Stock Transfer Report</span>
          <div style={{ flex: 1, minWidth: 4 }} />
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <Tooltip title="Refresh" arrow>
            <IconButton onClick={fetchReport} disabled={loading} size="small" sx={{ border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', height: 29, width: 29, color: '#fff', flexShrink: 0, '&:hover': { background: 'rgba(255,255,255,0.1)' } }}>
              <Refresh sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export Excel" arrow>
            <IconButton onClick={handleExport} disabled={!data.length} size="small" sx={{ border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', height: 29, width: 29, color: '#fff', flexShrink: 0, '&:hover': { background: 'rgba(255,255,255,0.1)' }, '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' } }}>
              <GetApp sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </div>

        {/* Summary bar */}
        {fetched && data.length > 0 && (
          <div style={{ flexShrink: 0, display: 'flex', gap: 16, padding: '8px 12px', background: '#f8fafc', borderBottom: '1px solid #f0f0f0', fontSize: 12 }}>
            <span style={{ color: '#6b7280' }}>Transfers: <strong style={{ color: PRIMARY }}>{totalTransfers}</strong></span>
            <span style={{ color: '#6b7280' }}>Grand Total: <strong style={{ color: '#059669' }}>{fmt(grandTotal)}</strong></span>
          </div>
        )}

        {/* Table */}
        <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 130px)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', minWidth: 700 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
              <tr>
                <th style={th}>Transfer No</th>
                <th style={th}>Date</th>
                <th style={th}>From Branch</th>
                <th style={th}>To Branch</th>
                <th style={th}>Status</th>
                <th style={thR}>Items</th>
                <th style={thR}>Total Qty</th>
                <th style={thR}>Total Amount</th>
              </tr>
            </thead>
            <tbody>
              {data.map((t, i) => {
                const sc = statusColor(t.status);
                return (
                  <>
                    {/* Main row */}
                    <tr
                      key={t.transferNumber}
                      style={{
                        background: i % 2 === 0 ? '#fff' : PRIMARY_LT,
                        borderBottom: '1px solid #f0f0f0',
                      }}
                    >
                      <td style={{ ...td, fontWeight: 600, color: PRIMARY }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <CompareArrows sx={{ fontSize: 14, color: PRIMARY }} />
                          {t.transferNumber || '-'}
                        </div>
                      </td>
                      <td style={td}>{fmtDate(t.date)}</td>
                      <td style={td}>{t.fromBranch || '-'}</td>
                      <td style={td}>{t.toBranch || '-'}</td>
                      <td style={td}>
                        <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, background: sc.bg, color: sc.color }}>
                          {t.status || 'Pending'}
                        </span>
                      </td>
                      <td style={{ ...tdR, color: '#6b7280' }}>{t.items.length}</td>
                      <td style={{ ...tdR, fontWeight: 600 }}>{fmtQty(t.totalQty)}</td>
                      <td style={{ ...tdR, fontWeight: 700, color: '#059669' }}>{fmt(t.totalAmount)}</td>
                    </tr>

                    {/* Items always visible */}
                    <tr key={`items-${t.id}`}>
                      <td colSpan={8} style={{ padding: 0, background: '#fce4ec', borderBottom: `2px solid ${PRIMARY}` }}>
                        <div style={{ padding: '10px 16px 10px 48px' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                            <thead>
                              <tr style={{ background: '#f3e5f5' }}>
                                <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: PRIMARY, fontSize: 11 }}>#</th>
                                <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: PRIMARY, fontSize: 11 }}>Item Name</th>
                                <th style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600, color: PRIMARY, fontSize: 11 }}>Qty</th>
                                <th style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600, color: PRIMARY, fontSize: 11 }}>Rate</th>
                                <th style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600, color: PRIMARY, fontSize: 11 }}>Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {t.items.map((item, idx) => (
                                <tr key={idx} style={{ borderBottom: '1px solid #dbeafe', background: idx % 2 === 0 ? '#fff' : '#f3e5f5' }}>
                                  <td style={{ padding: '5px 10px', color: '#9ca3af', fontSize: 11 }}>{idx + 1}</td>
                                  <td style={{ padding: '5px 10px', fontWeight: 500, color: '#1e293b' }}>{item.name}</td>
                                  <td style={{ padding: '5px 10px', textAlign: 'right', color: '#374151' }}>{fmtQty(item.qty)}</td>
                                  <td style={{ padding: '5px 10px', textAlign: 'right', color: '#374151' }}>{fmt(item.rate)}</td>
                                  <td style={{ padding: '5px 10px', textAlign: 'right', fontWeight: 600, color: '#059669' }}>{fmt(item.amount)}</td>
                                </tr>
                              ))}
                              <tr style={{ background: PRIMARY_LT, borderTop: `1px solid ${PRIMARY}88` }}>
                                <td colSpan={2} style={{ padding: '6px 10px', fontWeight: 700, color: PRIMARY, fontSize: 12 }}>Total</td>
                                <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 700, color: PRIMARY }}>{fmtQty(t.totalQty)}</td>
                                <td></td>
                                <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 700, color: '#059669' }}>{fmt(t.totalAmount)}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  </>
                );
              })}
              {data.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: '32px', color: '#9ca3af', fontSize: 13 }}>
                    {fetched ? 'No stock transfers found for the selected date range' : 'Select date range to load report'}
                  </td>
                </tr>
              )}
            </tbody>
            {data.length > 0 && (
              <tfoot>
                <tr style={{ background: PRIMARY_LT, color: PRIMARY, fontWeight: 700, position: 'sticky', bottom: 0 }}>
                  <td colSpan={5} style={{ ...td, textAlign: 'right', fontWeight: 700 }}>Grand Total</td>
                  <td style={{ ...tdR, fontWeight: 700 }}>{data.reduce((s,t) => s + t.items.length, 0)}</td>
                  <td style={{ ...tdR, fontWeight: 700 }}>{fmtQty(data.reduce((s,t) => s + t.totalQty, 0))}</td>
                  <td style={{ ...tdR, fontWeight: 700, color: '#059669' }}>{fmt(grandTotal)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default StockTransferReport;



